import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { PublicNav } from "@/components/PublicNav";

// Validation schemas
const emailSchema = z.string().trim().email("Please enter a valid email address").max(255, "Email must be less than 255 characters");
const passwordSchema = z.string().min(6, "Password must be at least 6 characters").max(72, "Password must be less than 72 characters");
const nameSchema = z.string().trim().min(2, "Name must be at least 2 characters").max(100, "Name is too long");

const signInSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Password is required"),
});

const signUpSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  name: nameSchema,
});

const Auth = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState<'signup' | 'signin'>('signup');
  const [errors, setErrors] = useState<{ email?: string; password?: string; name?: string }>({});
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedRedirect = searchParams.get("redirect") || "/";
  // Always land on course creation page (/) after login unless a specific redirect was requested (e.g. /learn, /profile)
  const redirectTo = requestedRedirect === "/dashboard" ? "/" : requestedRedirect;
  const reasonSignup = searchParams.get("reason") === "signup";
  const modeParam = searchParams.get("mode");
  const { toast } = useToast();

  useEffect(() => {
    if (reasonSignup) setMode("signup");
    else if (modeParam === "signin") setMode("signin");
  }, [reasonSignup, modeParam]);

  useEffect(() => {
    // Check if user is already logged in
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate(redirectTo);
      }
    });
  }, [navigate, redirectTo]);

  const validateForm = () => {
    const schema = mode === 'signup' ? signUpSchema : signInSchema;
    const result = schema.safeParse({ email, password, name: mode === 'signup' ? name : 'N/A' });
    
    if (!result.success) {
      const fieldErrors: { email?: string; password?: string; name?: string } = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as 'email' | 'password' | 'name';
        fieldErrors[field] = err.message;
      });
      setErrors(fieldErrors);
      return false;
    }
    
    setErrors({});
    return true;
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    try {
      const redirectUrl = (import.meta.env.VITE_SITE_URL || window.location.origin).trim().replace(/\/$/, '');
      
      if (mode === 'signup') {
        const { error, data } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            emailRedirectTo: `${redirectUrl}/`,
            data: {
              full_name: name,
            }
          },
        });

        if (error) throw error;
        
        toast({
          title: "Success!",
          description: "Check your email to confirm your account.",
        });
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });

        if (error) throw error;
        navigate(redirectTo);
      }
    } catch (err: any) {
      console.error("Auth error:", err);
      toast({
        variant: "destructive",
        title: mode === 'signup' ? "Error signing up" : "Error signing in",
        description: err.message || "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'azure') => {
    try {
      const redirectUrl = (import.meta.env.VITE_SITE_URL || window.location.origin).trim().replace(/\/$/, '');
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${redirectUrl}${redirectTo.startsWith("/") ? redirectTo : "/"}`,
        },
      });
      if (error) throw error;
    } catch (err: any) {
      console.error("Social login error:", err);
      toast({
        variant: "destructive",
        title: "Login Error",
        description: err.message,
      });
    }
  };

  return (
    <div className="public-page min-h-screen flex flex-col bg-background">
      <PublicNav />
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-[400px] space-y-8">
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-heading font-semibold tracking-tight text-foreground">
            {mode === 'signup' ? "Create an account" : "Log in"}
          </h1>
          <p className="text-muted-foreground">
            {reasonSignup
              ? "You've used your free course download. Create an account to continue creating courses."
              : mode === 'signup'
                ? "Sign up with email or continue with a provider below."
                : "Sign in to your account."}
          </p>
        </div>

        <div className="space-y-3">
          <Button 
            variant="outline" 
            className="w-full h-12 text-base font-medium border-border bg-card hover:bg-muted flex items-center justify-center gap-3"
            onClick={() => handleSocialLogin('google')}
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {mode === 'signup' ? "Sign up with Google" : "Log in with Google"}
          </Button>
          <Button 
            variant="outline" 
            className="w-full h-12 text-base font-medium border-border bg-card hover:bg-muted flex items-center justify-center gap-3"
            onClick={() => handleSocialLogin('azure')}
          >
            <svg viewBox="0 0 23 23" className="h-5 w-5" xmlns="http://www.w3.org/2000/svg">
              <path fill="#f3f2f2" d="M0 0h23v23H0z"/>
              <path fill="#f35325" d="M1 1h10v10H1z"/>
              <path fill="#81bc06" d="M12 1h10v10H12z"/>
              <path fill="#05a6f0" d="M1 12h10v10H1z"/>
              <path fill="#ffba08" d="M12 12h10v10H12z"/>
            </svg>
            {mode === 'signup' ? "Sign up with Microsoft" : "Log in with Microsoft"}
          </Button>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-4 text-muted-foreground font-medium tracking-widest">OR</span>
          </div>
        </div>

        <form onSubmit={handleAuth} className="space-y-5">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-foreground" htmlFor="email">
                Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors(prev => ({ ...prev, email: undefined }));
                }}
                className={`h-12 border-input focus:ring-ring focus:border-primary placeholder:text-muted-foreground ${errors.email ? "border-destructive" : ""}`}
                autoComplete="email"
                required
              />
              {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
            </div>

            {mode === 'signup' && (
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-foreground" htmlFor="name">
                  Name
                </label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) setErrors(prev => ({ ...prev, name: undefined }));
                  }}
                  className={`h-12 border-input focus:ring-ring focus:border-primary placeholder:text-muted-foreground ${errors.name ? "border-destructive" : ""}`}
                  required
                />
                {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-foreground" htmlFor="password">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors(prev => ({ ...prev, password: undefined }));
                  }}
                  className={`h-12 pr-10 border-input focus:ring-ring focus:border-primary placeholder:text-muted-foreground ${errors.password ? "border-destructive" : ""}`}
                  autoComplete={mode === 'signup' ? "new-password" : "current-password"}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-destructive mt-1">{errors.password}</p>}
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full h-12 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90" 
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {mode === 'signup' ? "Signing up..." : "Logging in..."}
              </>
            ) : (
              mode === 'signup' ? "Sign up" : "Log in"
            )}
          </Button>
        </form>

        <div className="text-center pt-2 space-y-3">
          {mode === 'signup' && (
            <p className="text-xs text-muted-foreground">
              By signing up, you agree to our{" "}
              <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
              {" "}and{" "}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            {mode === 'signup' ? (
              <>Already have an account? <button type="button" onClick={() => setMode('signin')} className="text-primary font-semibold hover:underline">Log in</button></>
            ) : (
              <>Don't have an account? <button type="button" onClick={() => setMode('signup')} className="text-primary font-semibold hover:underline">Sign up</button></>
            )}
          </p>
        </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
